// The tool registry (slice 05): everything that can sit on a bar, which bars
// it may sit on, and the standard menu it is always reachable from.
//
// The hard rule of design/07-ui-shell.md §2 is kept HERE: a tool exists only
// as an entry in this list, and every entry names a standard menu, so nothing
// can be bar-only. A player and a DM get the same list minus the DM-only
// entries — the roles differ in what the menus offer, never in arrangement.
//
// The panels themselves arrive with their slices; a tool not in this build
// is a placeholder that says so, so a placed tool reads as a promise, not a
// bug.
import { useEffect, useState } from 'react';
import { PALETTE, POWERS, POWER_LABELS, memberLabel, partyLine, roleWord, type Power, type SlotId, type TableMember } from '@dragons-table/vtt-shared';
import { useTableState, useViewing } from '../table/TableState';
import { createWatchInvite, handSession, listCoDms, listWatchInvites, nameCoDm, revokeWatchInvite, setMemberColour, setMyColour, takeTableBack, unnameCoDm, type CoDmCandidate, type WatchInvite } from '../table';
import { useShell } from './context';
import { useLook } from './useLook';
import type { ToolId } from './dockHandle';
import { LibraryPanel } from './panels/LibraryPanel';
import { ScenesPanel } from './panels/ScenesPanel';
import { PropertiesPanel } from './panels/PropertiesPanel';
import { DicePanel } from './panels/DicePanel';
import { InitiativeStrip } from './panels/InitiativeStrip';
import { TrackerPanel } from './panels/TrackerPanel';
import { CompendiumPanel } from './panels/CompendiumPanel';
import { TablesPanel } from './panels/TablesPanel';
import { NpcsPanel } from './panels/NpcsPanel';
import { DmScreenPanel } from './panels/DmScreenPanel';
import { LootPanel } from './panels/LootPanel';
import { HandoutsPanel } from './panels/HandoutsPanel';
import { CardsPanel } from './panels/CardsPanel';
import { NotebookPanel } from './panels/NotebookPanel';
import { SoundPanel } from './panels/SoundPanel';
import { DialoguePanel } from './panels/DialoguePanel';
import { TokensPanel } from './panels/TokensPanel';
import { ChecklistPanel } from './panels/ChecklistPanel';

export type StandardMenuId = 'table' | 'view' | 'dice' | 'combat' | 'layout';

export const STANDARD_MENUS: ReadonlyArray<{ id: StandardMenuId; label: string }> = [
  { id: 'table', label: 'Table' },
  { id: 'view', label: 'View' },
  { id: 'dice', label: 'Dice' },
  { id: 'combat', label: 'Combat' },
  { id: 'layout', label: 'Layout' },
];

export interface ToolDef {
  id: ToolId;
  title: string;
  menu: StandardMenuId;
  /** Where it may sit; a tool that cannot render in a bar is simply not offered there (design/07 Q5). */
  slots: readonly SlotId[];
  dmOnly?: boolean;
  /** Offered only at a practice table (qa-runs 1163). */
  practiceOnly?: boolean;
  /** A DM-only tool's power (issue 1159): a co-DM holding it is offered the tool; absent, the DM's alone. */
  power?: Power;
  /** One line for the placement chooser. */
  blurb: string;
}

const SIDE: readonly SlotId[] = ['left', 'right', 'center'];

export const TOOLS: readonly ToolDef[] = [
  { id: 'properties', title: 'Properties', menu: 'view', slots: SIDE,
    blurb: 'Click a token to see what you are allowed to see. With nothing selected, the DM sees the scene and a player their own character\'s token.' },
  { id: 'tokens', title: 'Tokens', menu: 'view', slots: SIDE,
    blurb: 'The tokens you can see as a list, with where each stands; your own move by the arrow keys. For the keyboard and screen readers.' },
  { id: 'people', title: 'People', menu: 'table', slots: SIDE,
    blurb: 'Who is at the table, their colours, and who is here now.' },
  { id: 'library', title: 'Library', menu: 'table', slots: SIDE, dmOnly: true, power: 'map',
    blurb: 'Your uploaded maps and tokens.' },
  { id: 'scenes', title: 'Scenes', menu: 'table', slots: SIDE, dmOnly: true, power: 'map',
    blurb: 'The table\'s scenes: stage one where only you can see it, then put it live for everyone.' },
  { id: 'dm-screen', title: 'DM screen', menu: 'table', slots: SIDE, dmOnly: true, power: 'notes',
    blurb: 'Your DM screen from the site, and the campaign\'s notes, quests and relationships. Yours alone: players never see it.' },
  { id: 'loot', title: 'Loot & shops', menu: 'table', slots: SIDE, dmOnly: true, power: 'notes',
    blurb: 'Roll a treasure hoard or stock a shop with the site\'s generators, open your saved shops, and give an item to a character on the board.' },
  { id: 'handouts', title: 'Handouts', menu: 'table', slots: SIDE,
    blurb: 'Handouts: the DM writes them and sends each to everyone or to chosen players; you read what you were given.' },
  { id: 'cards', title: 'Cards', menu: 'table', slots: SIDE,
    blurb: 'Card decks: the DM puts a deck on the table; everyone draws, plays, passes and shows from their own hand. Others see only how many cards you hold.' },
  { id: 'sound', title: 'Sound', menu: 'table', slots: SIDE,
    blurb: 'Music and ambience: the DM plays them for the table; everyone sets their own volume or mutes.' },
  { id: 'dialogue', title: 'Dialogue', menu: 'table', slots: SIDE,
    blurb: 'NPC dialog: every line an NPC said to you, newest at the bottom, to scroll back for a clue. The DM says lines here.' },
  { id: 'checklist', title: 'Checklist', menu: 'table', slots: SIDE, dmOnly: true, practiceOnly: true,
    blurb: 'The practice table\'s five steps and Reset.' },
  { id: 'notebook', title: 'Notebook', menu: 'table', slots: SIDE,
    blurb: 'Your own notes at this table. Each page is yours alone until you share it with the DM.' },
  { id: 'compendium', title: 'Compendium', menu: 'table', slots: SIDE,
    blurb: 'Search the site\'s rules mid-session; an entry opens on the site in a second window.' },
  { id: 'dice', title: 'Dice bag', menu: 'dice', slots: SIDE,
    blurb: 'Your dice, the roller and the shared roll log. Collapses to a die.' },
  { id: 'tables', title: 'Tables', menu: 'dice', slots: SIDE, dmOnly: true, power: 'notes',
    blurb: 'Rollable tables: the site\'s and your own. A roll posts to the log, private or public as the dice bag is set.' },
  { id: 'initiative', title: 'Initiative strip', menu: 'combat', slots: ['bottom'],
    blurb: 'The active turn and the next five. Start Encounter sits here when nothing is running.' },
  { id: 'tracker', title: 'Turn tracker', menu: 'combat', slots: SIDE,
    blurb: 'The full order: every combatant, reordering, effects that count down.' },
  { id: 'npcs', title: 'Campaign NPCs', menu: 'combat', slots: SIDE, dmOnly: true, power: 'notes',
    blurb: 'The campaign\'s NPCs from the site: place one on the board, hidden until you reveal it.' },
];

export const toolById = (id: ToolId): ToolDef | undefined => TOOLS.find((t) => t.id === id);

/**
 * The tools a person is offered: the DM every one; a co-DM (issue 1159) a DM-only tool only when they hold its power,
 * and no notebook (a co-DM sits at no seat, decision D on #1062); anyone else none of the DM's.
 */
export function toolsFor(role: string, powers?: readonly Power[] | null): ToolDef[] {
  if (role === 'codm') return TOOLS.filter((t) => t.id !== 'notebook' && (!t.dmOnly || (t.power !== undefined && !!powers?.includes(t.power))));
  return TOOLS.filter((t) => role === 'dm' || !t.dmOnly);
}

export const SLOT_LABELS: Record<SlotId, string> = { left: 'Left bar', right: 'Right bar', bottom: 'Bottom bar', center: 'Centre' };

// ── The panels ─────────────────────────────────────────────────────────────

function Placeholder({ what, arrives }: { what: string; arrives: string }) {
  return (
    <div className="p-3 text-sm text-stone-400 space-y-2">
      <p>{what}</p>
      <p className="text-xs text-stone-500">Arrives with {arrives}.</p>
    </div>
  );
}

/**
 * The roster: names, roles, colours, presence, the DM's colour override and lock (slice 03's surface, now a tool). Under
 * a player's name, the parties they sit in (issue 1579), when the table has more than one party this viewer is sent:
 * a campaign of one party looks as it did, and a player is sent no party but their own.
 */
export function PeoplePanel() {
  const { table: view, campaignId, onResult } = useShell();
  const { store } = useTableState();
  const look = useLook();
  const isDm = view.me.role === 'dm';
  // A person by their character and handle, never an account id (issue 1023).
  const label = (m: TableMember) => memberLabel(m, store.state.objects);
  const groups = (view.parties?.length ?? 0) > 1;
  return (
    <div className="p-2 text-sm" data-testid="people">
      <ul className="divide-y divide-stone-800" data-testid="members">
        {view.members.map((m) => (
          <li key={m.id} className="py-2 flex flex-wrap items-center gap-3" data-testid={`member-${m.id}`}>
            <span className="inline-block h-4 w-4 rounded-full" style={{ backgroundColor: m.colour ? look.identity(m.colour) : '#444' }} aria-hidden="true" data-colour={m.colour ? look.identity(m.colour) : undefined} />
            <span className="flex flex-col">
              <span>{label(m)}</span>
              {groups && m.role === 'player' && m.parties?.length ? <span className="text-xs text-stone-500" data-testid={`member-parties-${m.id}`}>{partyLine(m.parties)}</span> : null}
            </span>
            <span className="text-stone-500">{roleWord(m.role)}{m.present ? ' · here' : ''}{m.colourLocked ? ' · colour locked' : ''}</span>
            {isDm && m.role === 'player' && (
              <span className="ml-auto flex items-center gap-1">
                <select aria-label={`Colour for ${label(m)}`} className="rounded bg-stone-800 px-1 py-0.5" value={m.colour ?? ''}
                  onChange={async (e) => onResult(await setMemberColour(campaignId, m.id, e.target.value || null, m.colourLocked))}>
                  <option value="">—</option>
                  {PALETTE.map((c) => <option key={c.hex} value={c.hex}>{c.name}</option>)}
                </select>
                <button type="button" className="rounded border border-stone-600 px-2 py-0.5"
                  aria-label={`${m.colourLocked ? 'Unlock' : 'Lock'} colour for ${label(m)}`}
                  onClick={async () => onResult(await setMemberColour(campaignId, m.id, null, !m.colourLocked))}>
                  {m.colourLocked ? 'Unlock' : 'Lock'}
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {isDm && <GuestDm campaignId={campaignId} members={view.members} />}
      {isDm && <CoDms campaignId={campaignId} />}
      {isDm && <WatchInvites campaignId={campaignId} />}
    </div>
  );
}

/**
 * The DM's co-DMs (issue 1159; decision D on #1062): the campaign's co-DMs, each with "Give powers…" — the closed list
 * of powers as ticks, none ticked to begin with — or, once given, their powers to change and take away. One word for one
 * person (#1442, Tom's A): the campaign role is Co-DM, and the powers held here are shown beside it (`Co-DM · Tokens,
 * Dice`). A co-DM seated as a player cannot be given powers (one role per person at a table): the server says so in
 * words, shown here.
 */
const coDmStatus = (c: CoDmCandidate) => {
  if (c.powers) return `Co-DM · ${c.powers.length ? c.powers.map((p) => POWER_LABELS[p].label).join(', ') : 'no powers'}`;
  return c.seated ? 'Co-DM, seated as a player' : 'Co-DM';
};

export function CoDms({ campaignId }: { campaignId: string }) {
  const [list, setList] = useState<CoDmCandidate[] | null>(null);
  const [editing, setEditing] = useState<{ id: string; powers: Power[] } | null>(null);
  const [said, setSaid] = useState('');
  useEffect(() => {
    let live = true;
    listCoDms(campaignId).then((got) => { if (live) setList(got ?? []); });
    return () => { live = false; };
  }, [campaignId]);
  const nameOf = (c: CoDmCandidate) => (c.handle ? `@${c.handle}` : c.displayName ?? 'a co-DM');
  const save = async () => {
    if (!editing) return;
    const got = await nameCoDm(campaignId, editing.id, editing.powers);
    if ('refused' in got) { setSaid(got.refused); return; }
    setList(got.candidates); setEditing(null); setSaid('');
  };
  const remove = async (id: string) => {
    const got = await unnameCoDm(campaignId, id);
    if (got) { setList(got); setSaid(''); } else setSaid("That co-DM's powers could not be taken away just now.");
  };
  if (!list || list.length === 0) return null;
  return (
    <section className="mt-3 space-y-2 border-t border-stone-800 pt-3" data-testid="co-dms" aria-label="Co-DMs">
      <h3 className="vtt-section-head">Co-DMs</h3>
      <p className="text-xs text-stone-500">Give a co-DM powers at this table: they see and do only what you tick.</p>
      <ul className="space-y-2 text-xs">
        {list.map((c) => (
          <li key={c.id} className="space-y-1" data-testid={`co-dm-${c.id}`}>
            <div className="flex items-center gap-2">
              <span>{nameOf(c)}</span>
              <span className="text-stone-500">{coDmStatus(c)}</span>
              {editing?.id !== c.id && (
                <button type="button" className="ml-auto rounded border border-stone-600 px-2 py-0.5" onClick={() => { setEditing({ id: c.id, powers: c.powers ?? [] }); setSaid(''); }}>
                  {c.powers ? 'Powers…' : 'Give powers…'}
                </button>
              )}
              {c.powers && editing?.id !== c.id && (
                <button type="button" className="rounded border border-stone-600 px-2 py-0.5" aria-label={`Take away ${nameOf(c)}'s powers`} onClick={() => remove(c.id)}>Remove powers</button>
              )}
            </div>
            {editing?.id === c.id && (
              <fieldset className="space-y-1 rounded border border-stone-700 p-2" aria-label={`Powers for ${nameOf(c)}`}>
                {POWERS.map((p) => (
                  <label key={p} className="flex items-start gap-2">
                    <input type="checkbox" checked={editing.powers.includes(p)}
                      onChange={(e) => setEditing({ id: c.id, powers: e.target.checked ? [...editing.powers, p] : editing.powers.filter((x) => x !== p) })} />
                    <span><span className="text-stone-200">{POWER_LABELS[p].label}</span> <span className="text-stone-500">{POWER_LABELS[p].hint}</span></span>
                  </label>
                ))}
                <div className="flex gap-2">
                  <button type="button" className="rounded border border-amber-600 px-2 py-0.5" onClick={save}>{c.powers ? 'Save' : 'Give powers'}</button>
                  <button type="button" className="rounded border border-stone-600 px-2 py-0.5" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </fieldset>
            )}
          </li>
        ))}
      </ul>
      {said && <p role="status" className="text-xs text-amber-300">{said}</p>}
    </section>
  );
}

const candidateName = (c: { handle: string | null; displayName: string | null }) => (c.handle ? `@${c.handle}` : c.displayName ?? 'a co-DM');

/**
 * A guest DM for a session (issue 1160; binding decisions 15 and 28): "Hand the session to…" one of the campaign's
 * co-GMs not seated. The tokens and the hidden monsters always go across (Tom's A) — said, never offered as a tick —
 * and the DM chooses the two things that may be withheld: their notes and their hidden rolls. While a guest runs the
 * session: who, and "Take the table back". The members' list re-reads it when the server says the table changed.
 */
export function GuestDm({ campaignId, members }: { campaignId: string; members?: readonly TableMember[] }) {
  const [list, setList] = useState<CoDmCandidate[] | null>(null);
  const [choosing, setChoosing] = useState<{ id: string; notes: boolean; hiddenRolls: boolean } | null>(null);
  const [said, setSaid] = useState('');
  // Read again whenever the table's people change (issue 1434): a seat removed on the site, a co-DM named in the panel
  // above, a guest handed the session — any of them changes who can be handed it.
  const people = (members ?? []).map((m) => `${m.id}:${m.role}${m.guest ? ':guest' : ''}`).join(',');
  useEffect(() => {
    let live = true;
    listCoDms(campaignId).then((got) => { if (live) setList(got ?? []); });
    return () => { live = false; };
  }, [campaignId, people]);
  if (!list) return null;
  const eligible = list.filter((c) => !c.seated);
  const guest = list.find((c) => c.guest);
  if (!guest && eligible.length === 0) return null;
  const hand = async () => {
    if (!choosing) return;
    const got = await handSession(campaignId, choosing.id, { notes: choosing.notes, hiddenRolls: choosing.hiddenRolls });
    if ('refused' in got) { setSaid(got.refused); return; }
    setList(got.candidates); setChoosing(null); setSaid('');
  };
  const takeBack = async () => {
    const got = await takeTableBack(campaignId);
    if (got) { setList(got); setSaid(''); } else setSaid('The table could not be taken back just now.');
  };
  return (
    <section className="mt-3 space-y-2 border-t border-stone-800 pt-3 text-xs" data-testid="guest-dm" aria-label="Guest DM">
      <h3 className="vtt-section-head">Guest DM</h3>
      {guest ? (
        <div className="flex items-center gap-2">
          <span data-testid="guest-dm-running">{candidateName(guest)} is running this session.</span>
          <button type="button" className="ml-auto rounded border border-amber-600 px-2 py-0.5" onClick={takeBack}>Take the table back</button>
        </div>
      ) : choosing ? (
        <fieldset className="space-y-1 rounded border border-stone-700 p-2" aria-label="Hand the session over">
          <label className="flex items-center gap-2">
            <span>Hand the session to</span>
            <select aria-label="Guest DM" className="rounded bg-stone-800 px-1 py-0.5" value={choosing.id} onChange={(e) => setChoosing({ ...choosing, id: e.target.value })}>
              {eligible.map((c) => <option key={c.id} value={c.id}>{candidateName(c)}</option>)}
            </select>
          </label>
          <p className="text-stone-500">They run the tokens, the hidden monsters, the map and the dice. You keep seeing everything and can still act.</p>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={choosing.notes} onChange={(e) => setChoosing({ ...choosing, notes: e.target.checked })} />
            <span><span className="text-stone-200">Hand over my notes</span> <span className="text-stone-500">{POWER_LABELS.notes.hint}</span></span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={choosing.hiddenRolls} onChange={(e) => setChoosing({ ...choosing, hiddenRolls: e.target.checked })} />
            <span><span className="text-stone-200">Hand over my hidden rolls</span> <span className="text-stone-500">Your private rolls, before and during the session, and your fudges.</span></span>
          </label>
          <div className="flex gap-2">
            <button type="button" className="rounded border border-amber-600 px-2 py-0.5" onClick={hand}>Hand over</button>
            <button type="button" className="rounded border border-stone-600 px-2 py-0.5" onClick={() => setChoosing(null)}>Cancel</button>
          </div>
        </fieldset>
      ) : (
        <button type="button" className="rounded border border-stone-600 px-2 py-0.5" onClick={() => { setChoosing({ id: eligible[0]!.id, notes: false, hiddenRolls: false }); setSaid(''); }}>
          Hand the session to…
        </button>
      )}
      {said && <p role="status" className="text-amber-300">{said}</p>}
    </section>
  );
}

/**
 * The banner while a guest DM runs the session (issue 1160), for both DMs: the main DM's "<guest> is running this
 * session" with "Take the table back"; the guest's "You are running this session for <DM>". Nobody else's.
 */
export function GuestBanner({ campaignId, me, members }: { campaignId: string; me: { id: string; role: string }; members: readonly TableMember[] }) {
  const [said, setSaid] = useState('');
  const guest = members.find((m) => m.guest);
  const dm = members.find((m) => m.role === 'dm');
  if (!guest) return null;
  const name = (m: TableMember | undefined, fallback: string) => (m ? candidateName(m) : fallback);
  if (me.role === 'dm') {
    return (
      <p role="status" data-testid="guest-banner" className="flex flex-wrap items-center gap-2 border-b border-stone-800 bg-stone-900 px-3 py-1 text-sm text-amber-300">
        {name(guest, 'Your guest DM')} is running this session.
        <button type="button" className="rounded border border-amber-600 px-2 py-0.5 text-xs hover:bg-stone-800"
          onClick={async () => setSaid((await takeTableBack(campaignId)) ? '' : 'The table could not be taken back just now.')}>Take the table back</button>
        {said && <span className="text-xs">{said}</span>}
      </p>
    );
  }
  if (guest.id !== me.id) return null;
  return (
    <p role="status" data-testid="guest-banner" className="border-b border-stone-800 bg-stone-900 px-3 py-1 text-sm text-amber-300">
      You are running this session for {name(dm, 'the DM')}.
    </p>
  );
}

/**
 * The DM's invites to watch (issue 1040; COMM-009): a link anyone signed in may claim to watch this table, seeing
 * what a player who owns no tokens sees. The link is shown once, as it is made — only its hash is kept — with Copy;
 * the list says how many each seats, and Revoke ends those seats at once.
 */
function WatchInvites({ campaignId }: { campaignId: string }) {
  const [invites, setInvites] = useState<WatchInvite[] | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [said, setSaid] = useState('');
  useEffect(() => {
    let live = true;
    listWatchInvites(campaignId).then((list) => { if (live) setInvites(list ?? []); });
    return () => { live = false; };
  }, [campaignId]);
  const invite = async () => {
    const made = await createWatchInvite(campaignId);
    if ('refused' in made) { setSaid(made.refused); return; }
    setSaid('');
    setLink(made.url);
    setInvites((await listWatchInvites(campaignId)) ?? invites);
  };
  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); setSaid('Copied.'); } catch { setSaid('Select the link and copy it.'); }
  };
  const revoke = async (id: string) => {
    const list = await revokeWatchInvite(campaignId, id);
    if (list) setInvites(list); else setSaid('That invite could not be revoked just now.');
  };
  return (
    <section className="mt-3 space-y-2 border-t border-stone-800 pt-3" data-testid="watch-invites" aria-label="Watchers">
      <div className="flex items-center gap-2">
        <h3 className="vtt-section-head">Watchers</h3>
        <button type="button" className="ml-auto rounded border border-stone-600 px-2 py-0.5" onClick={invite}>Invite a watcher</button>
      </div>
      <p className="text-xs text-stone-500">A watcher sees what a player who owns no tokens sees, and changes nothing.</p>
      {link && (
        <div className="flex items-center gap-1" data-testid="watch-link">
          <input readOnly aria-label="Watch link" className="min-w-0 flex-1 rounded bg-stone-800 px-1 py-0.5 text-xs" value={link} onFocus={(e) => e.currentTarget.select()} />
          <button type="button" className="rounded border border-stone-600 px-2 py-0.5" onClick={copy}>Copy</button>
        </div>
      )}
      {said && <p role="status" className="text-xs text-stone-400">{said}</p>}
      {invites && invites.length > 0 && (
        <ul className="space-y-1 text-xs">
          {invites.map((i) => (
            <li key={i.id} className="flex items-center gap-2" data-testid={`watch-invite-${i.id}`}>
              <span>Made {new Date(i.created).toLocaleString()}</span>
              <span className="text-stone-500">{i.revoked ? 'revoked' : `${i.watching} watching`}</span>
              {!i.revoked && (
                <button type="button" className="ml-auto rounded border border-stone-600 px-2 py-0.5" aria-label={`Revoke the invite made ${new Date(i.created).toLocaleString()}`}
                  onClick={() => revoke(i.id)}>Revoke</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** A player's own colour, from the Table menu (design/08 Q4: changeable from the top bar). */
export function ColourChooser() {
  const { table: view, campaignId, onResult } = useShell();
  const look = useLook();
  const [busy, setBusy] = useState(false);
  useEffect(() => setBusy(false), [view.me.colour]);
  return (
    <div className="space-y-1">
      <p className="text-sm">Your colour{view.me.colourLocked ? ' (locked by the DM)' : ''}</p>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Your colour">
        {PALETTE.map((c) => (
          <button key={c.hex} type="button" aria-label={c.name} aria-pressed={view.me.colour === c.hex} disabled={view.me.colourLocked || busy}
            className={`h-7 w-7 rounded-full border-2 ${view.me.colour === c.hex ? 'border-white' : 'border-transparent'} disabled:opacity-50`}
            style={{ backgroundColor: look.identity(c.hex) }} onClick={async () => { setBusy(true); onResult(await setMyColour(campaignId, c.hex)); setBusy(false); }} />
        ))}
      </div>
    </div>
  );
}

/**
 * The body a tool shows in its slot. While the DM views the table as somebody (issue 1354), a tool that person does not
 * have shows a plain note in place of its controls: the panel stays where the DM put it (taking it off would rearrange,
 * and save, their layout), and its controls come back with "Back to your view".
 */
export function ToolBody({ tool }: { tool: ToolId }) {
  const { viewingAs = null, viewAs = () => undefined } = useViewing() ?? {};
  if (viewingAs && !toolsFor(viewingAs.role).some((t) => t.id === tool)) {
    const who = viewingAs.label ?? (viewingAs.handle ? `@${viewingAs.handle}` : 'This person');
    return (
      <div className="space-y-2 p-3 text-sm text-stone-400" data-testid={`not-theirs-${tool}`}>
        <p>{who} does not have {toolById(tool)?.title ?? 'this'}. It is yours again when you go back to your view.</p>
        <button type="button" className="rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800" onClick={() => viewAs(null)}>Back to your view</button>
      </div>
    );
  }
  return <ToolSwitch tool={tool} />;
}

function Checklist() {
  const { campaignId, onResult } = useShell();
  return <ChecklistPanel campaignId={campaignId} onResult={onResult} />;
}

function ToolSwitch({ tool }: { tool: ToolId }) {
  switch (tool) {
    case 'people': return <PeoplePanel />;
    case 'properties': return <PropertiesPanel />;
    case 'tokens': return <TokensPanel />;
    case 'library': return <LibraryPanel />;
    case 'scenes': return <ScenesPanel />;
    case 'dice': return <DicePanel />;
    case 'initiative': return <InitiativeStrip />;
    case 'tracker': return <TrackerPanel />;
    case 'compendium': return <CompendiumPanel />;
    case 'npcs': return <NpcsPanel />;
    case 'dm-screen': return <DmScreenPanel />;
    case 'loot': return <LootPanel />;
    case 'handouts': return <HandoutsPanel />;
    case 'cards': return <CardsPanel />;
    case 'notebook': return <NotebookPanel />;
    case 'sound': return <SoundPanel />;
    case 'dialogue': return <DialoguePanel />;
    case 'checklist': return <Checklist />;
    case 'tables': return <TablesPanel />;
    default: return <Placeholder what="This tool is not in this build." arrives="a later build" />;
  }
}
